/**
 * AbbeyZone Local Calculation Engine
 *
 * Mirrors the server-side stock_audit_engine.py logic for offline calculation.
 * Core formula:
 *   Opening Stock
 *   + Deliveries (posted during period, at cost)
 *   - Credits (valued at running WAC, NOT at line_total)
 *   - Cost of Sales (quantities sold converted to stock units × running WAC)
 *   - Adjustments (quantities × running WAC)
 *   = Expected Closing Stock
 *
 *   Actual Closing Stock = count-based valuation (count_qty converted to stock_units × running WAC)
 *   Variance = Actual - Expected
 *
 * CRITICAL RULES (must match server exactly):
 *   - Costs are PER STOCK UNIT (bottle, case, keg), NOT per ml/base unit.
 *   - Running WAC is tracked per product through: Opening → Deliveries → Credits → Sales → Adjustments.
 *   - Unit conversion uses: qty_in_stock_units = qty × (source_unit.conversion_to_base / stock_unit.conversion_to_base)
 *   - Sales are valued at COST (running WAC), not selling price.
 */

/**
 * Get the stock unit for a product.
 * Matches server's get_stock_unit: is_base_stock_unit → is_default_count_unit → first unit.
 */
function getStockUnit(product) {
  const units = product?.units || [];
  if (units.length === 0) return null;
  return units.find(u => u.is_base_stock_unit)
      || units.find(u => u.is_default_count_unit)
      || units[0];
}

/**
 * Convert a quantity from one unit to stock units.
 * Matches server's convert_to_stock_units.
 * @param {number} qty - Quantity in source units
 * @param {object|null} sourceUnit - The unit the quantity is expressed in
 * @param {object|null} stockUnit - The product's stock unit
 * @returns {number} Quantity in stock units
 */
function convertToStockUnits(qty, sourceUnit, stockUnit) {
  if (qty == null) return 0;
  if (!sourceUnit || !stockUnit) return qty;
  if (sourceUnit.id === stockUnit.id) return qty;

  const sourceConv = sourceUnit.conversion_to_base || 1;
  const stockConv = stockUnit.conversion_to_base || 1;
  if (stockConv === 0) return qty;
  return round4(qty * sourceConv / stockConv);
}

/**
 * Find a unit on a product by unit_id or unit_label.
 * Matches server's label-matching logic.
 */
function findUnit(product, unitId, unitLabel) {
  const units = product?.units || [];
  if (unitId) {
    const byId = units.find(u => u.id === unitId);
    if (byId) return byId;
  }
  if (unitLabel) {
    const labelLower = unitLabel.trim().toLowerCase();
    const byLabel = units.find(u => (u.unit_type || '').trim().toLowerCase() === labelLower);
    if (byLabel) return byLabel;
  }
  return null;
}

// ─── iter364 ── Recipe expansion (JS mirror of recipe_expansion.py) ──────
/**
 * Build the recipes-by-output-pid map from the offline package, using the
 * SAME resolution rules as load_recipes_by_output_pid in the backend:
 *   1. Use recipe.output_product_id when set.
 *   2. For post_mix recipes with NULL output_product_id, fall back to the
 *      first stockable ingredient (already pre-computed on the package as
 *      `effective_output_product_id`).
 * Last-write-wins on collision.
 */
function loadRecipesByOutputPid(recipes) {
  const out = {};
  (recipes || []).forEach(r => {
    if (!r || !r.is_active) return;
    const pid = r.effective_output_product_id != null
      ? r.effective_output_product_id
      : r.output_product_id;
    if (pid != null) out[pid] = r;
  });
  return out;
}

/**
 * Effective site override for a recipe (or null). Mirrors
 * `_site_override_for` in recipe_expansion.py — but the offline package
 * already trims `site_overrides` to the relevant site, so we just read it.
 */
function siteOverrideFor(recipe) {
  if (!recipe || !recipe.site_override || !recipe.site_override.is_active) return null;
  return recipe.site_override;
}

/**
 * Pure-function port of recipe_expansion.expand_sale. Given a sale of
 * `sale_qty` units of `recipe.effective_output_product_id`, returns an
 * array of ingredient deductions in the ingredient's STOCK units, plus
 * any warnings.
 */
function expandSale({ recipe, sale_qty, sale_measure_ml }) {
  const result = { deductions: [], warnings: [], serving_ratio: 1, site_override_applied: false };
  if (sale_qty == null || Number(sale_qty) === 0) return result;
  const saleQty = Number(sale_qty);
  const override = siteOverrideFor(recipe);
  result.site_override_applied = !!override;

  const base_serving_ml = recipe.default_serving_ml;
  const base_carb = recipe.carb_water_parts;
  const base_syrup = recipe.syrup_parts;
  let eff_serving_ml = (override && override.default_serving_ml != null) ? Number(override.default_serving_ml) : (base_serving_ml != null ? Number(base_serving_ml) : null);
  let eff_carb = (override && override.carb_water_parts != null) ? Number(override.carb_water_parts) : (base_carb != null ? Number(base_carb) : null);
  let eff_syrup = (override && override.syrup_parts != null) ? Number(override.syrup_parts) : (base_syrup != null ? Number(base_syrup) : null);

  if (override) {
    if (eff_carb != null && eff_carb <= 0) {
      result.warnings.push({
        code: 'recipe_site_override_invalid', severity: 'warning',
        recipe_id: recipe.id, recipe_name: recipe.recipe_name,
        message: `Site override has non-positive carb_water_parts (${eff_carb}); falling back to base recipe.`,
      });
      eff_carb = base_carb != null ? Number(base_carb) : null;
      eff_syrup = base_syrup != null ? Number(base_syrup) : null;
      eff_serving_ml = base_serving_ml != null ? Number(base_serving_ml) : null;
    }
    if (eff_syrup != null && eff_syrup <= 0) {
      result.warnings.push({
        code: 'recipe_site_override_invalid', severity: 'warning',
        recipe_id: recipe.id, recipe_name: recipe.recipe_name,
        message: `Site override has non-positive syrup_parts (${eff_syrup}); falling back to base recipe.`,
      });
      eff_carb = base_carb != null ? Number(base_carb) : null;
      eff_syrup = base_syrup != null ? Number(base_syrup) : null;
      eff_serving_ml = base_serving_ml != null ? Number(base_serving_ml) : null;
    }
  }

  // Serving ratio
  let serving_ratio = 1;
  if (sale_measure_ml != null && Number(sale_measure_ml) > 0 && eff_serving_ml != null && eff_serving_ml > 0) {
    serving_ratio = Number(sale_measure_ml) / eff_serving_ml;
  } else if (sale_measure_ml != null && (eff_serving_ml == null || eff_serving_ml <= 0)) {
    result.warnings.push({
      code: 'recipe_serving_unresolved', severity: 'warning',
      recipe_id: recipe.id, recipe_name: recipe.recipe_name,
      message: `Sale carries measure_ml=${sale_measure_ml} but recipe has no default_serving_ml. Treating ratio = 1.`,
    });
  }
  result.serving_ratio = serving_ratio;

  const is_post_mix = recipe.recipe_type === 'post_mix';
  (recipe.ingredients || []).forEach(ing => {
    if (ing.is_non_stock) {
      result.deductions.push({
        parent_recipe_id: recipe.id,
        parent_output_product_id: recipe.output_product_id,
        ingredient_product_id: null,
        ingredient_label: ing.ingredient_label,
        qty_in_stock_units: 0,
        is_non_stock: true,
      });
      return;
    }
    if (ing.ingredient_product_id == null) {
      result.warnings.push({
        code: 'recipe_ingredient_unmapped', severity: 'warning',
        recipe_id: recipe.id, recipe_name: recipe.recipe_name,
        ingredient_label: ing.ingredient_label,
        message: `Recipe '${recipe.recipe_name}' ingredient '${ing.ingredient_label}' has no product mapping; not deducted.`,
      });
      return;
    }
    let per_serving = ing.qty_in_stock_units_per_serving;
    if (per_serving == null) {
      result.warnings.push({
        code: 'recipe_ingredient_qty_unresolved', severity: 'warning',
        recipe_id: recipe.id, recipe_name: recipe.recipe_name,
        ingredient_label: ing.ingredient_label,
        message: `Ingredient '${ing.ingredient_label}' has no resolved qty_in_stock_units_per_serving — skipped.`,
      });
      return;
    }
    per_serving = Number(per_serving);
    if (!Number.isFinite(per_serving) || per_serving < 0) {
      // iter364 — defensive guard: a NaN / negative per_serving would
      // otherwise pollute sales_at_cost with NaN. Surface a warning and
      // skip the ingredient instead of corrupting the calculation.
      result.warnings.push({
        code: 'recipe_ingredient_qty_invalid', severity: 'warning',
        recipe_id: recipe.id, recipe_name: recipe.recipe_name,
        ingredient_label: ing.ingredient_label,
        message: `Ingredient '${ing.ingredient_label}' has an invalid qty_in_stock_units_per_serving (${ing.qty_in_stock_units_per_serving}) — skipped.`,
      });
      return;
    }

    // Site override re-derivation (post-mix only)
    if (
      override && is_post_mix && ing.qty_resolution_source === 'recipe_post_mix'
      && (override.carb_water_parts != null || override.syrup_parts != null || override.default_serving_ml != null)
      && recipe.syrup_box_litres != null && Number(recipe.syrup_box_litres) > 0
      && eff_serving_ml != null && eff_carb != null && eff_syrup != null && eff_syrup > 0
      && (eff_carb + eff_syrup) > 0
    ) {
      const ratio_total = (eff_carb + eff_syrup) / eff_syrup;
      const box_ml = Number(recipe.syrup_box_litres) * 1000;
      if (ratio_total > 0 && box_ml > 0) {
        per_serving = eff_serving_ml / ratio_total / box_ml;
      }
    }

    const waste = ing.waste_factor != null ? Number(ing.waste_factor) : 0;
    const ded = saleQty * serving_ratio * per_serving * (1 + waste);
    result.deductions.push({
      parent_recipe_id: recipe.id,
      parent_output_product_id: recipe.output_product_id,
      ingredient_product_id: ing.ingredient_product_id,
      ingredient_label: ing.ingredient_label,
      qty_in_stock_units: ded,
      is_non_stock: false,
    });
  });
  return result;
}

/**
 * Run a local stock audit calculation on an offline package.
 * Exactly mirrors server-side calculate_stock_audit in stock_audit_engine.py.
 *
 * @param {Object} pkg - The full offline session package from IndexedDB
 * @param {Array} localCountEntries - Current (possibly modified) count entries
 * @param {Array} localSalesEntries - Current (possibly modified) sales entries
 * @param {Array} localAdjustmentEntries - Current (possibly modified) adjustment entries
 * @returns {Object} Calculation result matching server snapshot shape
 */
export function calculateStockAuditLocally(pkg, localCountEntries, localSalesEntries, localAdjustmentEntries) {
  const products = pkg.products || [];
  const productMap = {};
  products.forEach(p => { productMap[p.id] = p; });

  // iter364 — Build recipe map from offline package. When the package is
  // older (v3) and doesn't ship `recipes`, this stays empty and the
  // engine behaves exactly as before.
  const recipesByOutputPid = loadRecipesByOutputPid(pkg.recipes || []);
  const ingredientStockMl = pkg.ingredient_stock_ml || {};
  const recipeExpandedOutputs = new Set();      // pids of recipe outputs whose consumption was deducted via ingredients
  const recipeParentIdByOutput = {};            // outputPid → parent_recipe_id
  const ingredientToParentRecipe = {};          // ingredientPid → parent_recipe_id
  const recipeWarnings = [];                    // surfaced into calculation_warnings

  // Per-product running WAC accumulators (matches server's prod_running)
  const prodRunning = {};
  function getRunning(pid) {
    if (!prodRunning[pid]) prodRunning[pid] = { qty: 0, val: 0, wac: 0 };
    return prodRunning[pid];
  }

  // Per-product movement accumulators
  const prodMovements = {};
  function getMov(pid) {
    if (!prodMovements[pid]) {
      prodMovements[pid] = {
        opening_qty: 0, opening_val: 0,
        del_qty: 0, del_val: 0,
        cred_qty: 0, cred_val: 0,
        sales_qty: 0, sales_val: 0,
        adj_qty: 0, adj_val: 0,
      };
    }
    return prodMovements[pid];
  }

  // ── 1. Opening Stock — per product ──
  let openingStockValue = 0;
  (pkg.opening_stock || []).forEach(os => {
    const val = os.value || 0;
    const qty = os.base_quantity || os.quantity || 0;
    openingStockValue += val;
    const m = getMov(os.beverage_product_id);
    m.opening_qty += qty;
    m.opening_val += val;
    // Initialize running WAC
    const r = getRunning(os.beverage_product_id);
    r.qty += qty;
    r.val += val;
    if (r.qty > 0) r.wac = round4(r.val / r.qty);
  });

  // ── 2. Deliveries — per product (sorted by date, then id) ──
  let deliveriesValue = 0;
  const allDeliveryLines = [];
  (pkg.deliveries || []).forEach(d => {
    (d.lines || []).forEach(dl => {
      allDeliveryLines.push({
        ...dl,
        delivery_date: d.delivery_date,
        delivery_id: d.id,
      });
    });
  });
  // Sort by date, then delivery_id, then line position (matches server ORDER BY)
  allDeliveryLines.sort((a, b) => {
    if (a.delivery_date !== b.delivery_date) return a.delivery_date < b.delivery_date ? -1 : 1;
    return (a.delivery_id || 0) - (b.delivery_id || 0);
  });

  allDeliveryLines.forEach(dl => {
    const pid = dl.beverage_product_id;
    const val = dl.line_total || 0;
    // Use stock_quantity if available, else raw quantity (matches server logic)
    const qty = dl.stock_quantity || dl.quantity || 0;
    deliveriesValue += val;
    const m = getMov(pid);
    m.del_qty += qty;
    m.del_val += val;
    // Update running WAC — inflow recalculates WAC
    const r = getRunning(pid);
    r.val += val;
    r.qty += qty;
    if (r.qty > 0) r.wac = round4(r.val / r.qty);
  });

  // ── 3. Credits — per product (valued at RUNNING WAC, not line_total) ──
  let creditsValue = 0;
  const allCreditLines = [];
  (pkg.credits || []).forEach(c => {
    (c.lines || []).forEach(cl => {
      allCreditLines.push({
        ...cl,
        credit_date: c.credit_date,
        credit_id: c.id,
      });
    });
  });
  allCreditLines.sort((a, b) => {
    if (a.credit_date !== b.credit_date) return a.credit_date < b.credit_date ? -1 : 1;
    return (a.credit_id || 0) - (b.credit_id || 0);
  });

  allCreditLines.forEach(cl => {
    const pid = cl.beverage_product_id;
    const qty = cl.stock_quantity || cl.quantity || 0;
    const r = getRunning(pid);
    // Value credit at running WAC (matches server: NOT line_total)
    const valAtWac = round2(qty * r.wac);
    creditsValue += valAtWac;
    const m = getMov(pid);
    m.cred_qty += qty;
    m.cred_val += valAtWac;
    // Update running — outflow: qty decreases, WAC unchanged
    r.qty -= qty;
    r.val -= valAtWac;
    if (r.qty > 0) r.wac = round4(r.val / r.qty);
  });

  // ── 4. Cost of Sales — value at running WAC (matches server) ──
  let salesAtCost = 0;
  let salesAtSelling = 0;

  (localSalesEntries || []).forEach(se => {
    if (!se.beverage_product_id) {
      // Unallocated sale — use stored value as cost
      salesAtCost += se.value || 0;
      return;
    }
    const pid = parseInt(se.beverage_product_id);
    const product = productMap[pid];
    const qty = se.quantity || 0;
    if (qty <= 0 && !se.value) return;

    salesAtSelling += se.value || 0;

    // ── iter364 Recipe Expansion ────────────────────────────────────────
    // If this product is a recipe OUTPUT, deduct the ingredients (BIB
    // syrup etc.) at running WAC and skip the normal parent-product
    // outflow. Mirrors stock_audit_engine.py lines 831–952.
    const recipe = recipesByOutputPid[pid];
    if (recipe) {
      // Resolve sale_measure_ml: prefer the product unit's conversion,
      // fall back to recipe.servings[label] / recipe.default_serving_ml.
      const saleUnit = findUnit(product, se.unit_id, se.unit_label);
      let saleMeasureMl = null;
      if (saleUnit && saleUnit.conversion_to_base) {
        saleMeasureMl = Number(saleUnit.conversion_to_base);
      } else if (se.unit_label) {
        const needle = String(se.unit_label).trim().toLowerCase();
        if (recipe.default_serving_label && String(recipe.default_serving_label).trim().toLowerCase() === needle) {
          if (recipe.default_serving_ml != null) saleMeasureMl = Number(recipe.default_serving_ml);
        }
        if (saleMeasureMl == null) {
          for (const s of (recipe.servings || [])) {
            if (s.serving_label && String(s.serving_label).trim().toLowerCase() === needle) {
              if (s.serving_ml != null) saleMeasureMl = Number(s.serving_ml);
              break;
            }
          }
        }
      }
      const expansion = expandSale({ recipe, sale_qty: qty, sale_measure_ml: saleMeasureMl });
      expansion.warnings.forEach(w => recipeWarnings.push(w));

      let expandedLineCost = 0;
      expansion.deductions.forEach(ded => {
        if (ded.is_non_stock || ded.ingredient_product_id == null) return;
        const ingPid = ded.ingredient_product_id;
        let ingQty = Number(ded.qty_in_stock_units);
        // BIB-fraction → ingredient stock-unit rescale (iter360e parity)
        if (recipe.syrup_box_litres != null && ingredientStockMl[ingPid] != null) {
          const boxMl = Number(recipe.syrup_box_litres) * 1000;
          const stockMl = Number(ingredientStockMl[ingPid]);
          if (boxMl > 0 && stockMl > 0 && stockMl !== boxMl) {
            ingQty = (ingQty * boxMl) / stockMl;
          }
        }
        if (ingQty <= 0) return;
        const ir = prodRunning[ingPid];
        let ingCost = 0;
        if (!ir) {
          recipeWarnings.push({
            code: 'recipe_ingredient_no_running_balance',
            severity: 'warning',
            recipe_id: ded.parent_recipe_id,
            ingredient_product_id: ingPid,
            message: `Recipe-expanded ingredient pid=${ingPid} has no opening/delivery on file. Cost of expansion line is £0.`,
          });
        } else {
          ingCost = round2(ingQty * (ir.wac || 0));
          ir.qty -= ingQty;
          ir.val -= ingCost;
          if (ir.qty > 0) ir.wac = round4(ir.val / ir.qty);
        }
        expandedLineCost += ingCost;
        const im = getMov(ingPid);
        im.sales_qty += ingQty;
        im.sales_val += ingCost;
        ingredientToParentRecipe[ingPid] = ded.parent_recipe_id;
      });
      // Tag the parent as suppressed and skip the normal parent decrement.
      recipeExpandedOutputs.add(pid);
      recipeParentIdByOutput[pid] = recipe.id;
      salesAtCost += expandedLineCost;
      return;
    }
    // ── End recipe expansion ────────────────────────────────────────────

    const stockUnit = getStockUnit(product);
    const saleUnit = findUnit(product, se.unit_id, se.unit_label);

    let qtyInStock;
    if (saleUnit && stockUnit) {
      qtyInStock = convertToStockUnits(qty, saleUnit, stockUnit);
    } else {
      // No matching unit found — treat qty as stock units (matches server fallback)
      qtyInStock = qty;
    }

    // Value sales at running WAC
    const r = getRunning(pid);
    const lineCost = round2(qtyInStock * r.wac);
    salesAtCost += lineCost;

    const m = getMov(pid);
    m.sales_qty += qtyInStock;
    m.sales_val += lineCost;
    // Update running — outflow
    r.qty -= qtyInStock;
    r.val -= lineCost;
    if (r.qty > 0) r.wac = round4(r.val / r.qty);
  });

  // ── 5. Adjustments — financial amounts (pre-signed) ──
  // 2026-04-21 rule 1 + 3 — split non-wastage adjustments:
  //   Management → NOW a retail inc-VAT figure. We strip VAT → A (sales ex
  //     VAT) and apply the site's COS budget % → B (management cost of
  //     sales). B flows into expected_closing / indirect COS exactly where
  //     the legacy `managementValue` did; A is subtracted from the raw
  //     result to produce the final surplus/shortage. (2026-04-24)
  //   Allowance  → EXCLUDED from COS / GP, applied directly to final result
  //   Wastage    → KPI-only (Phase 1 rule)
  let managementSalesInclVat = 0;  // Raw user inputs (retail inc VAT)
  let allowanceValue = 0;          // Deducted from final result
  let wastageValue = 0;
  // iter420i — Subset of wastage flagged include_in_result=True; flows
  // into the final-result bucket like allowances (mirrors backend).
  let wastageIncludedValue = 0;

  (localAdjustmentEntries || []).forEach(ae => {
    const v = parseFloat(ae.value) || 0;
    const type = (ae.adjustment_type || '').toString().trim().toLowerCase();
    if (type === 'wastage') {
      wastageValue += v;
      if (ae.include_in_result === true) {
        wastageIncludedValue += v;
      }
    } else if (type === 'allowance') {
      allowanceValue += v;
    } else if (type === 'management') {
      managementSalesInclVat += v;
    } else {
      // Unknown / legacy types default to management so historical data
      // without an explicit type keeps the old COS behaviour.
      managementSalesInclVat += v;
    }
  });

  // 2026-04-24 — derive A (ex-VAT) and B (mgmt cost of sales)
  const siteCosBudgetPctRaw = pkg.site?.budget_cost_of_sales_pct;
  const siteCosBudgetPct = siteCosBudgetPctRaw != null && siteCosBudgetPctRaw !== ''
    ? parseFloat(siteCosBudgetPctRaw)
    : null;
  const vatRateForMgmt = parseFloat(pkg.org_stocktaking_settings?.default_vat_rate ?? 20) || 20;
  const vatDivisor = 1 + (vatRateForMgmt / 100);
  const managementSalesExclVat = vatDivisor > 0
    ? round2(managementSalesInclVat / vatDivisor)
    : managementSalesInclVat;
  // NOTE: missing-cos-budget blocker is surfaced in calculationWarnings
  // below (severity=blocker) so the offline UI can mirror the online block.
  const managementCostOfSales = siteCosBudgetPct != null
    ? round2(managementSalesExclVat * (siteCosBudgetPct / 100))
    : 0;

  const managementValue = managementCostOfSales;      // B — flows into COS / expected_closing
  const adjustmentsValue = managementValue;            // legacy alias

  // ── 5b. Internal transfers (offline parity with backend engine) ──
  // iter421 — Compute transfers IN/OUT before expected_closing so cellar
  // child sessions (and any session whose package ships a `transfers`
  // list) get the same `expected = opening + deliveries + transfers_in
  // − credits − effective_sales + adjustments` formula the server uses.
  // Previously the offline engine ignored transfers when deriving
  // expected_closing, which meant Cellar's offline Calculate step
  // produced the wrong variance (it didn't subtract Issues To Locations).
  //
  // Detection rule mirrors backend:
  //   - session must be a multi_with_cellar child (parent_session_id +
  //     audit_mode), OR
  //   - the package ships a non-empty `transfers` list with an entry
  //     referencing this session's audit_location_id.
  // Cellar children typically have zero `sales_at_cost` (no sales
  // panel), so we use `transfers_out_value` as the effective outflow
  // when transfers exist.
  let transfersInValue = 0;
  let transfersOutValue = 0;
  {
    const currentLocId = pkg.session?.audit_location_id;
    const transfersList = pkg.transfers || [];
    const isMultiWithCellarChild = (
      pkg.session?.parent_session_id != null &&
      pkg.session?.audit_mode === 'multi_with_cellar'
    );
    if (currentLocId && transfersList.length) {
      for (const t of transfersList) {
        const tStatus = (t.status || '').toLowerCase();
        if (tStatus !== 'posted') continue;
        const isIn = t.to_location_id === currentLocId;
        const isOut = t.from_location_id === currentLocId;
        if (!isIn && !isOut) continue;
        for (const tl of (t.lines || [])) {
          const val = parseFloat(tl.total_value || 0) || 0;
          if (isIn) transfersInValue += val;
          else if (isOut) transfersOutValue += val;
        }
      }
    }
    transfersInValue = round2(transfersInValue);
    transfersOutValue = round2(transfersOutValue);
    // Note: isMultiWithCellarChild is informational; the `_has_transfers`
    // effective-flow switch is purely transfersList-driven so it stays
    // correct for both single-loc legacy data AND multi-loc child packs.
    void isMultiWithCellarChild;
  }

  // For multi_with_cellar child sessions, transfers replace sales as
  // the outflow (cellars don't sell; locations book sales separately
  // from cellar-to-location transfers).
  // Same equation as the server for every location: own sales AND transfers out, each counted once.
  const effectiveOutflowValue = salesAtCost + transfersOutValue;

  // ── 6. Expected Closing Stock ──
  // Management flows through expected_closing (signed). Allowances are
  // deliberately excluded — they only affect the final result.
  // Wastage stays excluded (Phase 1 methodology rule).
  // iter421 — Include transfers_in and use the effective outflow so
  // cellar Calculate matches backend math (was previously sales-only).
  const expectedClosingValue = round2(
    openingStockValue
    + deliveriesValue
    + transfersInValue
    - creditsValue
    - effectiveOutflowValue
    + managementValue
  );

  // ── 7. Actual Closing Stock (count-based valuation using running WAC) ──
  let actualClosingValue = 0;
  let lineCount = 0;
  const snapshotLines = [];

  (pkg.count_sheet_lines || []).forEach(line => {
    const entry = (localCountEntries || []).find(e => e.count_sheet_line_id === line.id);
    const pid = line.beverage_product_id;
    const product = productMap[pid];
    const stockUnit = getStockUnit(product);

    let countQty = 0;
    let countUnitObj = null;
    let countUnitLabel = null;

    if (entry && entry.quantity != null) {
      countQty = entry.quantity;
      countUnitObj = findUnit(product, entry.unit_id, entry.unit_label);
      countUnitLabel = entry.unit_label;
    }

    // If no count unit found, default to stock unit (matches server)
    if (!countUnitObj) countUnitObj = stockUnit;

    const qtyInStock = convertToStockUnits(countQty, countUnitObj, stockUnit);

    // Cost per stock unit — use running WAC (matches server)
    const r = getRunning(pid);
    const costPerStock = r.wac > 0 ? r.wac : 0;
    const costSource = r.wac > 0 ? 'running_wac' : 'missing';

    const lineVal = round2(qtyInStock * costPerStock);
    actualClosingValue += lineVal;

    if (countQty > 0) lineCount++;

    // Per-product variance (matches server)
    const mv = getMov(pid);
    const expectedQty = round4(mv.opening_qty + mv.del_qty - mv.cred_qty - mv.sales_qty - mv.adj_qty);
    const expectedVal = round2(mv.opening_val + mv.del_val - mv.cred_val - mv.sales_val - mv.adj_val);
    const varianceQty = round4(qtyInStock - expectedQty);
    const varianceVal = round2(lineVal - expectedVal);

    // Phase 1: per-product CONSUMPTION = opening + deliveries - credits - actual_closing
    // (Physical-stock-derived, does NOT use PLU sales)
    let consumptionQty = round4(mv.opening_qty + mv.del_qty - mv.cred_qty - qtyInStock);

    // iter364 — Recipe expansion: suppress consumption on the parent
    // OUTPUT (already accounted for via ingredient deductions) and tag
    // ingredients with parent recipe id. Mirrors stock_audit_engine.py
    // lines 1337–1350.
    let conversionSource = null;
    let parentRecipeId = null;
    let parentOutputProductId = null;
    if (recipeExpandedOutputs.has(pid)) {
      consumptionQty = 0;
      conversionSource = 'recipe_output_suppressed';
      parentRecipeId = recipeParentIdByOutput[pid] || null;
      parentOutputProductId = pid;
    } else if (ingredientToParentRecipe[pid] != null) {
      conversionSource = 'recipe_expanded';
      parentRecipeId = ingredientToParentRecipe[pid];
    }

    snapshotLines.push({
      session_count_sheet_line_id: line.id,
      beverage_product_id: pid,
      product_name_snapshot: line.product_name_snapshot,
      count_quantity: countQty,
      count_unit_id: countUnitObj?.id || null,
      count_unit_label: countUnitLabel || (countUnitObj?.unit_type) || null,
      base_quantity: qtyInStock,
      base_unit: stockUnit?.unit_type || 'unit',
      cost_per_base_unit: costPerStock,
      line_value: round2(lineVal),
      cost_source: costSource,
      expected_qty: expectedQty,
      expected_value: expectedVal,
      variance_qty: varianceQty,
      variance_value: varianceVal,
      consumption_qty: consumptionQty,
      conversion_source: conversionSource,
      parent_recipe_id: parentRecipeId,
      parent_output_product_id: parentOutputProductId,
    });
  });

  actualClosingValue = round2(actualClosingValue);
  const stocktakeResult = round2(actualClosingValue - expectedClosingValue);

  // ── 8. Methodology-aware outputs (Phase 4) ───────────────────────────
  // Mirror the server engine's dual methodology emission so the offline
  // Calculate & Review panel can render the exact same headline / blocker
  // banner / KPI block the online StockAuditResultsPanel renders.
  //
  // Indirect Cost of Sales (stock-movement derived, methodology-neutral).
  // 2026-04-21 rule 1 — Management is included (signed), Allowances are
  // EXCLUDED from COS (they flow into the final result instead), Wastage
  // remains KPI-only.
  //   COS_indirect = Opening + Deliveries + Transfers In − Credits
  //                − Transfers Out ± Management − Actual Closing
  //
  // iter383 — Offline multi-location audit support. When the package
  // ships a `transfers` list (bundled by `build_offline_package` for
  // multi-loc parents and propagated to each child via the offline
  // loader), transfers_in / transfers_out are computed in section 5b
  // above (so they can feed into expected_closing). costOfSalesIndirect
  // re-uses those values here.
  const costOfSalesIndirect = round2(
    openingStockValue
    + deliveriesValue
    + transfersInValue
    - creditsValue
    - transfersOutValue
    + managementValue
    - actualClosingValue
  );

  // Actual Receipts — read from session header or local_changes (persisted by
  // the Sales panel when user edits offline).
  const sessionHdr = pkg.session || {};
  const localChg = pkg.local_changes || {};
  const accountsSalesInclRaw = localChg.accounts_sales_incl_vat ?? sessionHdr.accounts_sales_incl_vat;
  const accountsSalesExclRaw = localChg.accounts_sales_excl_vat ?? sessionHdr.accounts_sales_excl_vat;
  const defaultVatRate = parseFloat(pkg.org_stocktaking_settings?.default_vat_rate ?? 20) || 20;
  const accountsSalesIncl = accountsSalesInclRaw != null && accountsSalesInclRaw !== ''
    ? parseFloat(accountsSalesInclRaw)
    : null;
  let accountsSalesExcl = accountsSalesExclRaw != null && accountsSalesExclRaw !== ''
    ? parseFloat(accountsSalesExclRaw)
    : null;
  if (accountsSalesExcl == null && accountsSalesIncl != null) {
    accountsSalesExcl = round2(accountsSalesIncl / (1 + defaultVatRate / 100));
  }

  // Estimated receipts preview = sales_at_selling excl VAT (PLU-derived).
  const estimatedReceiptsExcl = round2(salesAtSelling);
  // 2026-04-28 (iter329) — Apply org default VAT rate offline so the
  // wizard's headline tile and Yield % use the same incl-VAT figures as
  // the backend. Offline doesn't see the per-product session VAT rates,
  // so org default is the canonical fallback (matches what the backend
  // engine does for products without session sales entries).
  const orgVatRateRaw = pkg.org_stocktaking_settings?.default_vat_rate;
  const orgVatRate = orgVatRateRaw != null ? Number(orgVatRateRaw) : 20;
  const estimatedReceiptsIncl = round2(estimatedReceiptsExcl * (1 + orgVatRate / 100));

  const orgMethodology = (pkg.org_stocktaking_settings?.headline_methodology || 'legacy');

  // iter421 — Cellar children now calculate offline using the transfers
  // logic in section 5b above (effective_outflow_value, transfers_in/out
  // into expected_closing). Non-cellar children + single-site sessions
  // continue to use sales-driven outflow. Methodology selection below
  // mirrors the server engine.
  const stocktakeResultLegacy = stocktakeResult;
  let stocktakeResultRevised = null;
  if (accountsSalesIncl != null && estimatedReceiptsIncl != null) {
    // iter329 — revised methodology now incl-VAT both sides.
    stocktakeResultRevised = round2(accountsSalesIncl - estimatedReceiptsIncl);
  }

  // 2026-05-08 (iter340 v3) — Result formula:
  //   Final = Raw + Allowances − Management(inc VAT) + Included Wastage
  // Allowances are ADDED back per user directive so the surplus reflects
  // what was actually consumed including comped servings. Mgmt retail is
  // SUBTRACTED (retail diverted to staff/comps cannot count toward surplus).
  // iter420i — Wastage flagged `include_in_result=True` is added back
  // the same way allowances are (operator opt-in: recorded loss reduces
  // the measured deficit). Unflagged wastage stays KPI-only.
  // Mirrors backend stock_audit_engine.
  const stocktakeResultLegacyAdjusted = round2(
    stocktakeResultLegacy + allowanceValue + wastageIncludedValue - managementSalesInclVat
  );
  const stocktakeResultRevisedAdjusted = stocktakeResultRevised != null
    ? round2(stocktakeResultRevised + allowanceValue + wastageIncludedValue - managementSalesInclVat)
    : null;

  const calculationWarnings = [];
  // iter364 — Surface recipe-expansion warnings (unresolved ingredients,
  // missing running balance, invalid site override, etc.) into the same
  // calculation_warnings array the online engine emits.
  recipeWarnings.forEach(w => calculationWarnings.push(w));
  let headlineMethodology = orgMethodology === 'revised' ? 'revised' : 'legacy';
  let headlineFallbackReason = null;
  let headlineStocktakeResult = stocktakeResultLegacyAdjusted;

  // 2026-04-24 — if the auditor entered any Management retail but the
  // site has no COS Budget % configured we CANNOT compute the management
  // cost of sales. Surface a blocker so the offline wizard mirrors the
  // online 400 error behaviour.
  if (managementSalesInclVat !== 0 && siteCosBudgetPct == null) {
    calculationWarnings.push({
      code: 'management_cos_budget_required',
      severity: 'blocker',
      message:
        "Management retail has been entered but the site's COS Budget % is not "
        + 'configured. Set the Beverages COS Budget % on the Site Master before '
        + 'running this calculation.',
    });
    headlineStocktakeResult = null;
  }

  if (orgMethodology === 'revised') {
    if (accountsSalesIncl == null) {
      calculationWarnings.push({
        code: 'accounts_sales_required_offline',
        severity: 'blocker',
        message: 'Actual Receipts methodology is active but Actual Receipts (incl VAT) has not been entered. Enter it on the Sales step to complete the offline calculation.',
      });
      headlineStocktakeResult = null;
    } else {
      headlineStocktakeResult = stocktakeResultRevisedAdjusted;
    }
  }

  // Gross Profit — methodology-aware. Allowances are EXCLUDED (they don't
  // distort COS / GP — they adjust only the final surplus/shortage).
  const grossProfitLegacy = round2(salesAtSelling - salesAtCost);
  const gpPctLegacy = salesAtSelling > 0 ? round2((grossProfitLegacy / salesAtSelling) * 100) : null;
  let grossProfitRevised = null;
  let gpPctRevised = null;
  if (accountsSalesExcl != null) {
    grossProfitRevised = round2(accountsSalesExcl - costOfSalesIndirect);
    if (accountsSalesExcl > 0) {
      gpPctRevised = round2((grossProfitRevised / accountsSalesExcl) * 100);
    }
  }
  // 2026-02-16 — Global GP overhaul. GP is always Actual Receipts − Consumption
  // at Cost when accounts_sales_excl is present. Legacy GP is retained as a
  // diagnostic field only. Headline £ stocktake result remains methodology-driven
  // (Q1 a, user-confirmed).
  const headlineGrossProfit = grossProfitRevised !== null ? grossProfitRevised : grossProfitLegacy;
  const headlineGpPct = grossProfitRevised !== null ? gpPctRevised : gpPctLegacy;

  // 2026-05-07 (iter334) — Yield % denominator ADDS Management (inc VAT).
  // Mgmt stored negative → adding reduces the denominator by |Mgmt|.
  //   Yield = Actual / (Est + Mgmt) × 100
  let yieldPct = null;
  const yieldEstReceipts = estimatedReceiptsIncl != null
    ? estimatedReceiptsIncl
    : (estimatedReceiptsExcl != null ? estimatedReceiptsExcl : salesAtSelling);
  const yieldActualReceipts = accountsSalesIncl != null
    ? accountsSalesIncl
    : (accountsSalesExcl != null ? accountsSalesExcl : salesAtSelling);
  const yieldMgmt = round2(managementSalesInclVat || 0);
  const yieldDenominator = yieldEstReceipts + yieldMgmt;
  if (yieldDenominator > 0) {
    yieldPct = round2((yieldActualReceipts / yieldDenominator) * 100);
  }

  // 2026-04-21 rule 2 — Stockholding Days.
  //   Avg Daily COS = headline COS ÷ session period days
  //   Stockholding Days = Actual Closing Stock ÷ Avg Daily COS
  let stockholdingDays = null;
  let avgDailyCos = null;
  let periodDays = null;
  const sessPF = sessionHdr.period_from;
  const sessPT = sessionHdr.period_to;
  if (sessPF && sessPT) {
    const d1 = new Date(sessPF);
    const d2 = new Date(sessPT);
    // iter340 v4 — period_days excludes the day of the count, and
    // stockholding is rounded to whole days. Mirrors backend stock_audit_engine.
    periodDays = Math.max(1, Math.round((d2 - d1) / 86_400_000));
    const cosForDays = (accountsSalesExcl != null && costOfSalesIndirect != null)
      ? costOfSalesIndirect
      : (headlineMethodology === 'revised' ? costOfSalesIndirect : salesAtCost);
    if (cosForDays > 0 && periodDays > 0) {
      avgDailyCos = round2(cosForDays / periodDays);
      if (avgDailyCos > 0 && actualClosingValue > 0) {
        stockholdingDays = Math.round(actualClosingValue / avgDailyCos);
      }
    }
  }

  return {
    calculation_type: pkg.session?.session_type || 'stock_audit',
    total_value: actualClosingValue,
    line_count: lineCount,
    cost_basis_method: 'weighted_average',
    opening_stock_value: round2(openingStockValue),
    deliveries_value: round2(deliveriesValue),
    credits_value: round2(creditsValue),
    sales_at_cost: round2(salesAtCost),
    sales_at_selling: round2(salesAtSelling),
    sales_at_selling_excl_vat: round2(salesAtSelling),
    sales_value: round2(salesAtCost),
    adjustments_value: round2(adjustmentsValue),
    management_value: round2(managementValue),
    allowance_value: round2(allowanceValue),
    wastage_value: round2(wastageValue),
    // iter420i — Subset of wastage that was included in the result.
    wastage_included_value: round2(wastageIncludedValue),
    // 2026-04-24 — Management retail overhaul
    management_sales_incl_vat: round2(managementSalesInclVat),
    management_sales_excl_vat: round2(managementSalesExclVat),
    management_cost_of_sales: round2(managementCostOfSales),
    management_cos_budget_pct: siteCosBudgetPct,
    management_vat_rate: vatRateForMgmt,
    transfers_in_value: transfersInValue,
    transfers_out_value: transfersOutValue,
    expected_closing_value: expectedClosingValue,
    // Legacy result kept as primary `stocktake_result` for backward compat
    // with downstream callers (sync payload, historical report UI).
    stocktake_result: stocktakeResult,
    variance: stocktakeResult,
    // Phase 4 methodology-aware fields (mirror server engine)
    cost_of_sales_indirect: costOfSalesIndirect,
    accounts_sales_incl_vat: accountsSalesIncl,
    accounts_sales_excl_vat: accountsSalesExcl,
    estimated_receipts_excl_vat: estimatedReceiptsExcl,
    estimated_receipts_incl_vat: estimatedReceiptsIncl,
    stocktake_result_legacy: stocktakeResultLegacy,
    stocktake_result_revised: stocktakeResultRevised,
    // 2026-04-21 rule 3 — allowance-adjusted final figures
    stocktake_result_legacy_adjusted: stocktakeResultLegacyAdjusted,
    stocktake_result_revised_adjusted: stocktakeResultRevisedAdjusted,
    headline_methodology: headlineMethodology,
    headline_fallback_reason: headlineFallbackReason,
    headline_stocktake_result: headlineStocktakeResult,
    gross_profit_legacy: grossProfitLegacy,
    gp_pct_legacy: gpPctLegacy,
    gross_profit_revised: grossProfitRevised,
    gp_pct_revised: gpPctRevised,
    headline_gross_profit: headlineGrossProfit,
    headline_gp_pct: headlineGpPct,
    // 2026-04-21 rule 2 — Stockholding Days KPI
    stockholding_days: stockholdingDays,
    period_days: periodDays,
    avg_daily_cos: avgDailyCos,
    // 2026-04-24 iter315 — Yield % (Expected / Actual × 100) for offline parity
    yield_pct: yieldPct,
    calculation_warnings: calculationWarnings,
    lines: snapshotLines,
    calculated_at: new Date().toISOString(),
    is_offline_calculation: true,
  };
}

/**
 * Validate a stock audit session before/after calculation.
 * Mirrors server-side validation checks.
 */
export function validateStockAuditLocally(pkg, localCountEntries) {
  const errors = [];
  const warnings = [];
  const info = [];

  const session = pkg.session || {};
  const lines = pkg.count_sheet_lines || [];

  // Period dates
  if (!session.period_from || !session.period_to) {
    errors.push({ severity: 'error', category: 'session', message: 'Session period dates (From/To) must be set.' });
  }
  if (session.period_from && session.period_to && session.period_from > session.period_to) {
    errors.push({ severity: 'error', category: 'session', message: 'Period "From" date is after "To" date.' });
  }

  // Count entries — iter S2 (2026-10) parity: a counted entry is one with
  // a quantity recorded at all (0 is a valid "counted to zero" entry, same
  // definition as the server's `CountEntry.quantity.isnot(None)` check in
  // validation_engine.py / `_assert_has_closing_counts`).
  const counted = (localCountEntries || []).filter(e => e.quantity != null);
  if (counted.length === 0) {
    errors.push({
      severity: 'error',
      category: 'entry_quality',
      code: 'no_closing_counts',
      message: `No closing count entries exist for this session (0/${lines.length} lines counted). Calculate is disabled until at least one product has a counted quantity.`,
    });
  } else if (counted.length < lines.length) {
    warnings.push({
      severity: 'warning',
      category: 'closing_stock',
      message: `${counted.length}/${lines.length} products counted (${Math.round(counted.length / lines.length * 100)}%). Uncounted products treated as zero.`,
    });
  }

  // Opening stock
  const openingStock = pkg.opening_stock || [];
  if (openingStock.length === 0) {
    warnings.push({ severity: 'warning', category: 'opening_stock', message: 'No opening stock data. This may be the first session for this site.' });
  }

  // Zero-value products
  const products = pkg.products || [];
  const productMap = {};
  products.forEach(p => { productMap[p.id] = p; });

  const productCosts = pkg.product_costs || [];
  const costMap = {};
  productCosts.forEach(pc => { costMap[pc.beverage_product_id] = pc; });

  const noCostProducts = lines.filter(l => {
    const pc = costMap[l.beverage_product_id];
    return !pc || (!pc.weighted_average_cost && !pc.last_delivery_cost);
  });
  if (noCostProducts.length > 0) {
    warnings.push({
      severity: 'warning',
      category: 'costing',
      message: `${noCostProducts.length} product(s) have no cost data and will be valued at £0.00.`,
    });
  }

  return { errors, warnings, info, valid: errors.length === 0 };
}


/**
 * Generate comprehensive verification & validation notes for an offline stock audit.
 * Mirrors the backend validation_engine.py logic exactly.
 *
 * Runs both pre-calculation checks and post-calculation checks (when calcResult is provided).
 *
 * @param {Object} pkg - The full offline session package
 * @param {Array} localCountEntries - Current count entries
 * @param {Array} localSalesEntries - Current sales entries
 * @param {Array} localAdjustmentEntries - Current adjustment entries
 * @param {Object|null} calcResult - Result from calculateStockAuditLocally (null = pre-calc only)
 * @returns {Object} Validation report matching server shape:
 *   { valid, error_count, warning_count, info_count, total_issues, errors, warnings, info }
 */
export function generateVerificationNotes(pkg, localCountEntries, localSalesEntries, localAdjustmentEntries, calcResult) {
  const errors = [];
  const warnings = [];
  const info = [];

  const session = pkg.session || {};
  const lines = pkg.count_sheet_lines || [];
  const products = pkg.products || [];
  const productMap = {};
  products.forEach(p => { productMap[p.id] = p; });

  const openingStock = pkg.opening_stock || [];
  const osProductIds = new Set(openingStock.map(os => os.beverage_product_id));

  // ════════════════════════════════════════════
  //  1. DATA INTEGRITY CHECKS
  // ════════════════════════════════════════════

  // 1a. Period dates
  if (!session.period_from || !session.period_to) {
    errors.push({
      severity: 'error', category: 'data_integrity',
      message: 'Session is missing period dates (period_from / period_to). Deliveries, credits, and sales cannot be scoped correctly.',
      resolution: 'Edit the session and set both period_from and period_to dates.',
    });
  } else if (session.period_from > session.period_to) {
    errors.push({
      severity: 'error', category: 'data_integrity',
      message: `period_from (${session.period_from}) is after period_to (${session.period_to}).`,
      resolution: 'Correct the period dates so period_from <= period_to.',
    });
  }

  // 1b. Opening stock
  if (openingStock.length === 0) {
    warnings.push({
      severity: 'warning', category: 'data_integrity',
      message: 'No opening stock found for this session. Expected closing will only reflect in-period movements.',
      resolution: 'Use "Re-populate" to carry forward from a prior session, or verify this is the first session for this site.',
    });
  } else {
    // Zero-value opening stock items
    const zeroValOs = openingStock.filter(os => !os.value || parseFloat(os.value) === 0);
    if (zeroValOs.length > 0) {
      warnings.push({
        severity: 'warning', category: 'data_integrity',
        message: `${zeroValOs.length} opening stock item(s) have zero value. This may indicate missing cost data in the prior session.`,
        resolution: "Review the prior session's calculation to ensure all products had valid costs.",
      });
    }

    // Products counted but not in opening stock
    const countedWithQty = (localCountEntries || []).filter(e => e.quantity != null && e.quantity > 0);
    const countedProductIds = new Set();
    for (const ce of countedWithQty) {
      const line = lines.find(l => l.id === ce.count_sheet_line_id);
      if (line) countedProductIds.add(line.beverage_product_id);
    }
    for (const pid of countedProductIds) {
      if (!osProductIds.has(pid)) {
        const p = productMap[pid];
        const pname = p ? p.product_name : `Product #${pid}`;
        info.push({
          severity: 'info', category: 'data_integrity',
          message: `${pname} is counted but has no opening stock entry — treated as zero opening.`,
          product_id: pid, product_name: pname,
        });
      }
    }
  }

  // 1c. Delivery info (from package data)
  const deliveries = pkg.deliveries || [];
  const totalDeliveryLines = deliveries.reduce((sum, d) => sum + (d.lines || []).length, 0);
  if (session.period_from && session.period_to) {
    info.push({
      severity: 'info', category: 'data_integrity',
      message: `Period ${session.period_from} to ${session.period_to}: ${deliveries.length} posted delivery/deliveries (${totalDeliveryLines} lines) included in calculation.`,
    });
  }

  // ════════════════════════════════════════════
  //  2. ENTRY QUALITY CHECKS
  // ════════════════════════════════════════════

  let linesWithCount = 0;
  let linesWithoutCount = 0;

  for (const ln of lines) {
    const entry = (localCountEntries || []).find(e => e.count_sheet_line_id === ln.id);
    const product = productMap[ln.beverage_product_id];
    const pname = ln.product_name_snapshot || (product ? product.product_name : `Product #${ln.beverage_product_id}`);

    if (!entry || entry.quantity == null) {
      linesWithoutCount++;
      continue;
    }

    linesWithCount++;
    const qty = parseFloat(entry.quantity);

    // 2a. Negative quantities
    if (qty < 0) {
      errors.push({
        severity: 'error', category: 'entry_quality',
        message: `${pname}: Negative count quantity (${qty}). Count quantities must be zero or positive.`,
        product_id: ln.beverage_product_id, product_name: pname, line_id: ln.id,
        resolution: 'Correct the count entry to a non-negative value.',
      });
    }

    // 2b. Product exists
    if (!product) {
      errors.push({
        severity: 'error', category: 'entry_quality',
        message: `Count sheet line #${ln.id} references product #${ln.beverage_product_id} which does not exist in the product master.`,
        line_id: ln.id,
        resolution: 'Remove this line or re-create the count sheet.',
      });
    }
  }

  // Count coverage
  const totalLines = lines.length;
  if (linesWithoutCount > 0 && totalLines > 0) {
    const pct = Math.round(linesWithCount / totalLines * 100);
    const level = pct < 80 ? 'warning' : 'info';
    const msg = `${linesWithCount}/${totalLines} lines counted (${pct}% coverage). ${linesWithoutCount} line(s) have no count entry.`;
    const item = {
      severity: level, category: 'entry_quality', message: msg,
    };
    if (pct < 80) item.resolution = 'Enter counts for all products to ensure a complete audit.';
    (level === 'warning' ? warnings : info).push(item);
  }

  // Zero count — blocking error (parity: validation_engine.py `no_closing_counts`)
  if (linesWithCount === 0 && totalLines > 0) {
    errors.push({
      severity: 'error', category: 'entry_quality', code: 'no_closing_counts',
      message: `No closing count entries exist for this session (0/${totalLines} lines counted). Calculate is disabled until at least one product has a counted quantity.`,
      resolution: 'Go back to the Closing Stock step and enter counts for at least some products.',
    });
  }

  // ════════════════════════════════════════════
  //  3. UNIT CONVERSION CHECKS
  // ════════════════════════════════════════════

  for (const ln of lines) {
    const entry = (localCountEntries || []).find(e => e.count_sheet_line_id === ln.id);
    if (!entry || entry.quantity == null) continue;
    const product = productMap[ln.beverage_product_id];
    if (!product) continue;
    const pname = ln.product_name_snapshot || product.product_name;
    const units = product.units || [];
    const stockUnit = getStockUnit(product);

    // 3a. Multi-unit product without explicit unit selection
    if (units.length > 1 && !entry.unit_id && !entry.unit_label) {
      warnings.push({
        severity: 'warning', category: 'unit_conversion',
        message: `${pname}: Product has multiple units (${units.map(u => u.unit_type).join(', ')}) but no unit was selected on the count entry. Defaulting to stock unit.`,
        product_id: product.id, product_name: pname, line_id: ln.id,
        resolution: 'Select the correct counting unit (e.g. bottle or case) on the count entry.',
      });
    }

    // 3b. Selected unit not found
    if (entry.unit_label && units.length > 0) {
      const labelLower = entry.unit_label.trim().toLowerCase();
      const matched = units.some(u => (u.unit_type || '').trim().toLowerCase() === labelLower);
      if (!matched) {
        warnings.push({
          severity: 'warning', category: 'unit_conversion',
          message: `${pname}: Count unit "${entry.unit_label}" not found in product unit list (${units.map(u => u.unit_type).join(', ')}). Treated as stock units.`,
          product_id: product.id, product_name: pname, line_id: ln.id,
          resolution: "Update the count entry unit to match one of the product's configured units.",
        });
      }
    }

    // 3c. No units configured
    if (units.length === 0) {
      info.push({
        severity: 'info', category: 'unit_conversion',
        message: `${pname}: No units of measure configured. Using raw quantity.`,
        product_id: product.id, product_name: pname,
      });
    }

    // 3d. Stock unit has no conversion factor
    if (stockUnit && (stockUnit.conversion_to_base == null || parseFloat(stockUnit.conversion_to_base) === 0)) {
      warnings.push({
        severity: 'warning', category: 'unit_conversion',
        message: `${pname}: Stock unit "${stockUnit.unit_type}" has no conversion factor (conversion_to_base is zero/null). Unit conversion may be unreliable.`,
        product_id: product.id, product_name: pname,
        resolution: "Set conversion_to_base on the product's stock unit in the Product Master.",
      });
    }
  }

  // ════════════════════════════════════════════
  //  4. ADJUSTMENT CHECKS
  // ════════════════════════════════════════════

  (localAdjustmentEntries || []).forEach(adj => {
    // 4a. Adjustment with no value
    if (!adj.value || parseFloat(adj.value) === 0) {
      let pname = null;
      if (adj.beverage_product_id) {
        const p = productMap[parseInt(adj.beverage_product_id)];
        pname = p ? p.product_name : null;
      }
      warnings.push({
        severity: 'warning', category: 'entry_quality',
        message: `Adjustment "${adj.description || adj.adjustment_type || 'unnamed'}" has zero value and will not affect the calculation.`,
        product_id: adj.beverage_product_id ? parseInt(adj.beverage_product_id) : null,
        product_name: pname,
        resolution: 'Enter a value or remove this adjustment.',
      });
    }

    // 4b. Product-linked adjustment with quantity but no unit
    if (adj.beverage_product_id && adj.quantity && !adj.unit_label) {
      const p = productMap[parseInt(adj.beverage_product_id)];
      if (p && p.units && p.units.length > 1) {
        warnings.push({
          severity: 'warning', category: 'unit_conversion',
          message: `Adjustment for ${p.product_name}: quantity entered without unit for multi-unit product.`,
          product_id: parseInt(adj.beverage_product_id), product_name: p.product_name,
          resolution: 'Select the unit of measure for this adjustment.',
        });
      }
    }
  });

  // ════════════════════════════════════════════
  //  5. POST-CALCULATION CHECKS (costing + balance)
  // ════════════════════════════════════════════

  if (calcResult) {
    const snapLines = calcResult.lines || [];

    // 5a. Products with missing cost
    const missingCostLines = snapLines.filter(sl => ['no_cost', 'missing'].includes(sl.cost_source) || !sl.cost_source);
    for (const sl of missingCostLines) {
      errors.push({
        severity: 'error', category: 'costing',
        message: `${sl.product_name_snapshot}: No cost data available. Line valued at \u00A30.00.`,
        product_id: sl.beverage_product_id, product_name: sl.product_name_snapshot,
        resolution: 'Post a delivery for this product at this site to establish a cost, or set a manual cost override.',
      });
    }

    // 5b. Cost source distribution
    const sourceCounts = {};
    for (const sl of snapLines) {
      const src = sl.cost_source || 'unknown';
      sourceCounts[src] = (sourceCounts[src] || 0) + 1;
    }
    const sourceKeys = Object.keys(sourceCounts);
    if (sourceKeys.length > 1 && sourceCounts['running_wac']) {
      const nonWac = sourceKeys.filter(k => k !== 'running_wac');
      if (nonWac.length > 0) {
        const parts = nonWac.map(k => `${sourceCounts[k]} line(s) via ${k.replace(/_/g, ' ')}`).join(', ');
        info.push({
          severity: 'info', category: 'costing',
          message: `Mixed cost sources detected: ${sourceCounts['running_wac']} line(s) via WAC, ${parts}.`,
          resolution: 'This is expected when some products lack sufficient delivery history. Review cost sources in the variance table.',
        });
      }
    }

    // 5c. Large per-product variance
    for (const sl of snapLines) {
      const expected = parseFloat(sl.expected_qty || 0);
      const actual = parseFloat(sl.base_quantity || 0);
      if (expected > 0) {
        const pct = Math.abs(actual - expected) / expected * 100;
        if (pct > 50) {
          const level = pct > 100 ? 'warning' : 'info';
          (level === 'warning' ? warnings : info).push({
            severity: level, category: 'balance',
            message: `${sl.product_name_snapshot}: ${Math.round(pct)}% variance between expected (${expected.toFixed(4)}) and counted (${actual.toFixed(4)}).`,
            product_id: sl.beverage_product_id, product_name: sl.product_name_snapshot,
            resolution: 'Verify the count and check for unrecorded deliveries, credits, or sales.',
          });
        }
      }
    }

    // 5d. Overall variance check
    const expVal = parseFloat(calcResult.expected_closing_value || 0);
    const actVal = parseFloat(calcResult.total_value || 0);
    if (expVal > 0) {
      const variancePct = Math.abs(actVal - expVal) / expVal * 100;
      if (variancePct > 10) {
        warnings.push({
          severity: 'warning', category: 'balance',
          message: `Overall stocktake variance is ${variancePct.toFixed(1)}% (\u00A3${(actVal - expVal).toFixed(2)}). This may indicate unrecorded movements.`,
          resolution: 'Review all deliveries, credits, sales, and adjustments for completeness. Check for unposted transactions.',
        });
      }
    }
  } else {
    info.push({
      severity: 'info', category: 'costing',
      message: 'No calculation has been run yet. Post-calculation checks (costing, balance) will appear after calculation.',
    });
  }

  return {
    valid: errors.length === 0,
    error_count: errors.length,
    warning_count: warnings.length,
    info_count: info.length,
    total_issues: errors.length + warnings.length + info.length,
    errors,
    warnings,
    info,
  };
}

function round2(val) {
  // Match Python's Decimal ROUND_HALF_UP: 0.005 → 0.01, 0.015 → 0.02
  if (val >= 0) {
    return Math.floor(val * 100 + 0.5 + 1e-10) / 100;
  }
  return -Math.floor(-val * 100 + 0.5 + 1e-10) / 100;
}

function round4(val) {
  // Use 6 decimal places to match backend WAC precision
  if (val >= 0) {
    return Math.floor(val * 1000000 + 0.5 + 1e-10) / 1000000;
  }
  return -Math.floor(-val * 1000000 + 0.5 + 1e-10) / 1000000;
}
